import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Activity, Wind, Zap, Thermometer, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { liveMetrics, performanceHistory, monthlyProduction, structuralHealth, fleetData } from '../mock';

const Platform = () => {
  const [currentMetrics, setCurrentMetrics] = useState(liveMetrics);
  const [animatingMetrics, setAnimatingMetrics] = useState({});

  // Simulate live data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetrics(prev => ({
        ...prev,
        currentPower: (prev.currentPower + (Math.random() - 0.5) * 0.5).toFixed(2),
        windSpeed: (prev.windSpeed + (Math.random() - 0.5) * 0.3).toFixed(2),
        tsr: (prev.tsr + (Math.random() - 0.5) * 0.1).toFixed(2),
        efficiency: (prev.efficiency + (Math.random() - 0.5) * 0.5).toFixed(1),
        todayEnergy: (parseFloat(prev.todayEnergy) + 0.1).toFixed(1)
      }));
      
      // Trigger animation
      setAnimatingMetrics({
        currentPower: true,
        windSpeed: true,
        tsr: true,
        efficiency: true
      });
      
      setTimeout(() => setAnimatingMetrics({}), 500);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'status-optimal';
      case 'maintenance': return 'status-warning';
      case 'alert': return 'status-alert';
      default: return 'status-normal';
    }
  };

  return (
    <div className="platform-page">
      {/* Header */}
      <section className="page-header">
        <div className="container">
          <div className="header-content">
            <div className="header-icon">
              <Activity size={48} />
            </div>
            <h1 className="page-title">Live Monitoring Platform</h1>
            <p className="page-subtitle">
              Real-time turbine performance, structural health, and fleet management
            </p>
            <Badge className={`status-badge ${getStatusColor(currentMetrics.status)}`}>
              <CheckCircle className="mr-2 h-4 w-4" />
              System {currentMetrics.status.toUpperCase()}
            </Badge>
          </div>
        </div>
      </section>

      {/* Live Metrics Dashboard */}
      <section className="metrics-section">
        <div className="container">
          <div className="metrics-grid">
            <Card className={`metric-card ${animatingMetrics.currentPower ? 'metric-updating' : ''}`}>
              <div className="metric-header">
                <div className="metric-icon power">
                  <Zap />
                </div>
                <span className="metric-label">Live Power Output</span>
              </div>
              <div className="metric-value">{currentMetrics.currentPower} <span className="metric-unit">kW</span></div>
              <div className="metric-trend positive">↑ Generating</div>
            </Card>

            <Card className={`metric-card ${animatingMetrics.windSpeed ? 'metric-updating' : ''}`}>
              <div className="metric-header">
                <div className="metric-icon wind">
                  <Wind />
                </div>
                <span className="metric-label">Wind Speed</span>
              </div>
              <div className="metric-value">{currentMetrics.windSpeed} <span className="metric-unit">m/s</span></div>
              <div className="metric-trend positive">Good conditions</div>
            </Card>

            <Card className={`metric-card ${animatingMetrics.tsr ? 'metric-updating' : ''}`}>
              <div className="metric-header">
                <div className="metric-icon tsr">
                  <TrendingUp />
                </div>
                <span className="metric-label">Tip-Speed Ratio</span>
              </div>
              <div className="metric-value">{currentMetrics.tsr} <span className="metric-unit">TSR</span></div>
              <div className="metric-trend positive">Optimal range</div>
            </Card>

            <Card className={`metric-card ${animatingMetrics.efficiency ? 'metric-updating' : ''}`}>
              <div className="metric-header">
                <div className="metric-icon efficiency">
                  <Activity />
                </div>
                <span className="metric-label">Efficiency</span>
              </div>
              <div className="metric-value">{currentMetrics.efficiency} <span className="metric-unit">%</span></div>
              <div className="metric-trend positive">High performance</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-icon temperature">
                  <Thermometer />
                </div>
                <span className="metric-label">Temperature</span>
              </div>
              <div className="metric-value">{currentMetrics.temperature} <span className="metric-unit">°C</span></div>
              <div className="metric-trend normal">Normal</div>
            </Card>

            <Card className="metric-card energy">
              <div className="metric-header">
                <span className="metric-label">Energy Production</span>
              </div>
              <div className="energy-stats">
                <div className="energy-item">
                  <span className="energy-label">Today</span>
                  <span className="energy-value">{currentMetrics.todayEnergy} kWh</span>
                </div>
                <div className="energy-item">
                  <span className="energy-label">This Month</span>
                  <span className="energy-value">{currentMetrics.monthEnergy} kWh</span>
                </div>
                <div className="energy-item">
                  <span className="energy-label">Lifetime</span>
                  <span className="energy-value">{currentMetrics.lifetimeEnergy} MWh</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Detailed Analytics */}
      <section className="analytics-section">
        <div className="container">
          <Tabs defaultValue="performance" className="analytics-tabs">
            <TabsList className="tabs-list">
              <TabsTrigger value="performance">Performance Analytics</TabsTrigger>
              <TabsTrigger value="structural">Structural Health</TabsTrigger>
              <TabsTrigger value="financial">Financial Dashboard</TabsTrigger>
              <TabsTrigger value="fleet">Fleet Management</TabsTrigger>
            </TabsList>

            {/* Performance Tab */}
            <TabsContent value="performance" className="tab-content">
              <div className="analytics-grid">
                <Card className="chart-card">
                  <h3 className="chart-title">24-Hour Performance Profile</h3>
                  <div className="chart-container">
                    <div className="chart-legend">
                      <span className="legend-item power">Power Output (kW)</span>
                      <span className="legend-item wind">Wind Speed (m/s)</span>
                      <span className="legend-item tsr">TSR</span>
                    </div>
                    <div className="line-chart">
                      {performanceHistory.map((point, idx) => (
                        <div key={idx} className="chart-point-group">
                          <div className="chart-bars">
                            <div 
                              className="chart-bar power-bar" 
                              style={{ height: `${(point.power / 5) * 100}%` }}
                              title={`Power: ${point.power} kW`}
                            ></div>
                            <div 
                              className="chart-bar wind-bar" 
                              style={{ height: `${(point.windSpeed / 12) * 100}%` }}
                              title={`Wind: ${point.windSpeed} m/s`}
                            ></div>
                            <div 
                              className="chart-bar tsr-bar" 
                              style={{ height: `${(point.tsr / 5) * 100}%` }}
                              title={`TSR: ${point.tsr}`}
                            ></div>
                          </div>
                          <span className="chart-label">{point.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                <Card className="stats-card">
                  <h3 className="chart-title">Performance Metrics</h3>
                  <div className="stats-list">
                    <div className="stat-item">
                      <span className="stat-label">Average Power (24h)</span>
                      <span className="stat-value">3.2 kW</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Peak Power</span>
                      <span className="stat-value">4.2 kW</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Capacity Factor</span>
                      <span className="stat-value">68%</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Uptime</span>
                      <span className="stat-value success">99.8%</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Avg TSR</span>
                      <span className="stat-value">3.4</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Avg Efficiency</span>
                      <span className="stat-value success">38.9%</span>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Structural Health Tab */}
            <TabsContent value="structural" className="tab-content">
              <div className="structural-grid">
                <Card className="health-card">
                  <h3 className="chart-title">Structural Integrity Monitoring</h3>
                  <div className="health-metrics">
                    <div className="health-item">
                      <div className="health-header">
                        <span className="health-label">Blade Strain</span>
                        <Badge variant="outline" className="badge-success">Normal</Badge>
                      </div>
                      <div className="health-bar-container">
                        <div 
                          className="health-bar success" 
                          style={{ width: `${structuralHealth.bladeStrain * 100 / 0.005}%` }}
                        ></div>
                      </div>
                      <span className="health-value">{structuralHealth.bladeStrain}</span>
                    </div>

                    <div className="health-item">
                      <div className="health-header">
                        <span className="health-label">Vibration Level</span>
                        <Badge variant="outline" className="badge-success">Low</Badge>
                      </div>
                      <div className="health-bar-container">
                        <div 
                          className="health-bar success" 
                          style={{ width: `${structuralHealth.vibrationLevel * 100}%` }}
                        ></div>
                      </div>
                      <span className="health-value">{structuralHealth.vibrationLevel}</span>
                    </div>

                    <div className="health-item">
                      <div className="health-header">
                        <span className="health-label">Counterweight Balance</span>
                        <Badge variant="outline" className="badge-success">Excellent</Badge>
                      </div>
                      <div className="health-bar-container">
                        <div 
                          className="health-bar success" 
                          style={{ width: `${structuralHealth.counterweightBalance}%` }}
                        ></div>
                      </div>
                      <span className="health-value">{structuralHealth.counterweightBalance}%</span>
                    </div>

                    <div className="health-item">
                      <div className="health-header">
                        <span className="health-label">Tower Integrity</span>
                        <Badge variant="outline" className="badge-success">Excellent</Badge>
                      </div>
                      <div className="health-bar-container">
                        <div 
                          className="health-bar success" 
                          style={{ width: `${structuralHealth.towerIntegrity}%` }}
                        ></div>
                      </div>
                      <span className="health-value">{structuralHealth.towerIntegrity}%</span>
                    </div>
                  </div>
                </Card>

                <Card className="alerts-card">
                  <h3 className="chart-title">System Alerts & Maintenance</h3>
                  {structuralHealth.alerts.length === 0 ? (
                    <div className="no-alerts">
                      <CheckCircle className="check-icon" size={48} />
                      <p>No active alerts</p>
                      <span className="alert-subtitle">All systems operating normally</span>
                    </div>
                  ) : (
                    <div className="alerts-list">
                      {structuralHealth.alerts.map((alert, idx) => (
                        <div key={idx} className="alert-item">
                          <AlertCircle className="alert-icon" />
                          <span>{alert}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="maintenance-schedule">
                    <h4>Next Scheduled Maintenance</h4>
                    <p className="maintenance-date">June 15, 2026</p>
                    <p className="maintenance-type">Routine inspection & lubrication</p>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Financial Dashboard Tab */}
            <TabsContent value="financial" className="tab-content">
              <div className="financial-grid">
                <Card className="revenue-card">
                  <h3 className="chart-title">Monthly Revenue Analysis</h3>
                  <div className="revenue-chart">
                    {monthlyProduction.map((month, idx) => {
                      const maxRevenue = Math.max(...monthlyProduction.map(m => m.revenue));
                      const height = (month.revenue / maxRevenue) * 100;
                      return (
                        <div key={idx} className="revenue-bar-container">
                          <div 
                            className="revenue-bar" 
                            style={{ height: `${height}%` }}
                            title={`€${month.revenue}`}
                          >
                            <span className="bar-tooltip">€{month.revenue}</span>
                          </div>
                          <span className="revenue-label">{month.month}</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                <Card className="financial-stats">
                  <h3 className="chart-title">Financial Metrics</h3>
                  <div className="financial-metrics">
                    <div className="financial-item highlight">
                      <span className="financial-label">Total Revenue (12mo)</span>
                      <span className="financial-value">€{monthlyProduction.reduce((sum, m) => sum + m.revenue, 0).toLocaleString()}</span>
                    </div>
                    <div className="financial-item">
                      <span className="financial-label">Average Monthly Revenue</span>
                      <span className="financial-value">€{Math.round(monthlyProduction.reduce((sum, m) => sum + m.revenue, 0) / 12)}</span>
                    </div>
                    <div className="financial-item">
                      <span className="financial-label">Total Energy Sold</span>
                      <span className="financial-value">{monthlyProduction.reduce((sum, m) => sum + m.energy, 0).toLocaleString()} kWh</span>
                    </div>
                    <div className="financial-item">
                      <span className="financial-label">ROI Progress</span>
                      <span className="financial-value success">Year 2 of 5.2</span>
                    </div>
                    <div className="financial-item">
                      <span className="financial-label">Cumulative Profit</span>
                      <span className="financial-value success">€7,824</span>
                    </div>
                    <div className="financial-item">
                      <span className="financial-label">Payback Remaining</span>
                      <span className="financial-value">€12,176</span>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Fleet Management Tab */}
            <TabsContent value="fleet" className="tab-content">
              <Card className="fleet-card">
                <h3 className="chart-title">Fleet Overview</h3>
                <div className="fleet-summary">
                  <div className="fleet-stat">
                    <span className="fleet-number">4</span>
                    <span className="fleet-label">Total Turbines</span>
                  </div>
                  <div className="fleet-stat success">
                    <span className="fleet-number">3</span>
                    <span className="fleet-label">Optimal</span>
                  </div>
                  <div className="fleet-stat warning">
                    <span className="fleet-number">1</span>
                    <span className="fleet-label">Maintenance</span>
                  </div>
                  <div className="fleet-stat">
                    <span className="fleet-number">11.4 kW</span>
                    <span className="fleet-label">Combined Output</span>
                  </div>
                </div>

                <div className="fleet-list">
                  {fleetData.map(turbine => (
                    <div key={turbine.id} className={`fleet-item ${turbine.status}`}>
                      <div className="fleet-info">
                        <div className="fleet-name">{turbine.name}</div>
                        <div className="fleet-location">
                          Lat: {turbine.lat.toFixed(4)}, Lng: {turbine.lng.toFixed(4)}
                        </div>
                      </div>
                      <div className="fleet-status">
                        <Badge className={getStatusColor(turbine.status)}>
                          {turbine.status}
                        </Badge>
                      </div>
                      <div className="fleet-power">
                        <span className="power-value">{turbine.power} kW</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Platform;
